import { type RefObject, useCallback, useRef, useState } from 'react';

import { generateBenefitsPdfBlob } from '@/modules/autoVerify/benefitsPage/helpers/benefitsPdfGenerator';

export interface UseBenefitsPdfReturn {
  contentRef: RefObject<HTMLDivElement | null>;
  generatePdfFromDom: () => Promise<Blob>;
  isCapturing: boolean;
}

/**
 * Hook that provides DOM-based PDF generation for the benefits page.
 * Holds contentRef, isCapturing state, and a callback that captures the
 * ref's content and returns a PDF blob. Use with BenefitsPdfContext.
 */
export const useBenefitsPdf = (): UseBenefitsPdfReturn => {
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [isCapturing, setIsCapturing] = useState(false);

  const generatePdfFromDom = useCallback(async (): Promise<Blob> => {
    const element = contentRef.current;
    if (!element) {
      throw new Error('Benefits content ref not set');
    }
    setIsCapturing(true);
    try {
      return await generateBenefitsPdfBlob(element);
    } finally {
      setIsCapturing(false);
    }
  }, []);

  return {
    contentRef,
    isCapturing,
    generatePdfFromDom,
  };
};
